import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

const AdminPopularProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopFavorited();
  }, []);

  const fetchTopFavorited = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/products/top-favorited");
      setProducts(response.data || []);
    } catch (error) {
      console.error("❌ Error fetching top favorited products:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + "₫";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen font-inter">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800">Sản phẩm Yêu thích Nhất</h1>
          <p className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider mt-1">
            Top 10 sản phẩm được khách hàng quan tâm nhất
          </p>
        </div>
        <button 
          onClick={fetchTopFavorited}
          className="flex items-center justify-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
        >
          <span className="material-symbols-outlined text-[20px]">refresh</span>
          Làm mới
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {products.length > 0 ? (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-900 border-b border-slate-800">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Hạng</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Sản phẩm</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Lượt yêu thích</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Giá bán</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Trạng thái</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {products.map((product, index) => (
                  <tr key={product._id} className="hover:bg-blue-50/20 transition-colors group">
                    <td className="px-6 py-5">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-black text-sm
                        ${index === 0 ? "bg-yellow-400 text-yellow-900" : 
                          index === 1 ? "bg-slate-300 text-slate-700" : 
                          index === 2 ? "bg-orange-300 text-orange-900" : 
                          "bg-gray-100 text-gray-400"}
                      `}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0 group-hover:scale-105 transition-transform duration-300">
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = "https://placehold.co/100x100?text=No+Image" }}
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-black text-slate-800 leading-tight line-clamp-1">{product.name}</p>
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[9px] font-black uppercase tracking-wider shrink-0">
                              {product.category}
                            </span>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">MÃ: {product._id?.toString().slice(-8).toUpperCase()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-1.5 text-red-500 mb-1">
                          <span className="material-symbols-outlined fill-current text-[20px]">favorite</span>
                          <span className="text-lg font-black">{product.favoriteCount || 0}</span>
                        </div>
                        <div className="w-24 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                          <div 
                            className="bg-red-500 h-full rounded-full transition-all duration-1000" 
                            style={{ width: `${Math.min(100, (product.favoriteCount / (products[0]?.favoriteCount || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <p className="text-sm font-black text-slate-800">{formatCurrency(product.price)}</p>
                      {product.originalPrice > product.price && (
                        <p className="text-[10px] font-bold text-slate-400 line-through decoration-red-400/50">
                          {formatCurrency(product.originalPrice)}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-5 text-right">
                      {product.stock > 0 ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-[10px] font-black uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                          Còn hàng ({product.stock})
                        </span>
                      ) : (
                        <span className="text-red-500 text-[10px] font-black uppercase tracking-wider">Hết hàng</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-[48px] text-slate-200">sentiment_neutral</span>
              </div>
              <h3 className="text-slate-800 font-black text-lg">Chưa có dữ liệu yêu thích</h3>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mt-1 text-center">
                Danh sách sẽ được cập nhật khi có người dùng nhấn yêu thích sản phẩm
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-lg font-black uppercase tracking-wider mb-2">Lời khuyên cho Admin</h3>
            <p className="text-sm font-medium text-blue-100 mb-4 opacity-90 leading-relaxed">
              Những sản phẩm này đang dẫn đầu xu hướng quan tâm của khách hàng. Bạn nên cân nhắc đưa chúng vào mục <b>"Sản phẩm nổi bật"</b> hoặc <b>"Bộ sưu tập mới"</b> trên Homepage để tăng tỉ lệ chuyển đổi.
            </p>
            <Link to="/admin" className="inline-flex items-center bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
              Về Dashboard
            </Link>
          </div>
          <span className="material-symbols-outlined absolute -bottom-4 -right-4 text-[120px] text-white/10 rotate-12 select-none">Tips_and_Updates</span>
        </div>
        
        <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl shadow-slate-900/20 relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-lg font-black uppercase tracking-wider mb-2">Chiến dịch marketing</h3>
            <p className="text-sm font-medium text-slate-400 mb-4 leading-relaxed">
              Bạn có thể tạo các mã <b>Voucher</b> dành riêng cho những sản phẩm này để thúc đẩy khách hàng hoàn tất việc mua sắm các món đồ họ đã "thả tim".
            </p>
            <Link to="/admin/vouchers" className="inline-flex items-center bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-red-600/30">
              Quản lý Voucher
            </Link>
          </div>
          <span className="material-symbols-outlined absolute -bottom-4 -right-4 text-[120px] text-white/5 -rotate-12 select-none">Campaign</span>
        </div>
      </div>
    </div>
  );
};

export default AdminPopularProducts;
